import styled from 'styled-components';
import enhanceNegativeSign from '@paradigm/utils/src/enhanceNegativeSign';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { lima, razzmatazz } from '@paradigm/design-system/src/colors';
import { useRfqContext } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import {
  useMyAggAsks,
  useMyAggBids,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-contexts';
import { useFormattedGrfqQuotePrice } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import {
  useUMPlatformKillDisabled,
  useUMPlatformCreationDisabled,
} from '@paradigm/features/src/platform-state/contexts/providers';
import KillQuoteButton from '../rfqs/action/KillQuoteButton';
import {
  MyAsk,
  MyBid,
  MyOrdersContainer,
  KillCell,
  PriceCell,
  QtyCell,
  NumAsks,
  NumBids,
} from '../components/RfqRowBase';

interface Props {
  quoteIndex: number;
}

export default function MyOrders({ quoteIndex }: Props) {
  const { rfq } = useRfqContext();
  const isDated =
    rfq == null ? false : isDatedRfqLegs(rfq.legs, rfq.product_codes[0]);
  const bids = useMyAggBids();
  const asks = useMyAggAsks();

  const bid = bids[quoteIndex];
  const ask = asks[quoteIndex];

  const formattedBidPrice = useFormattedGrfqQuotePrice(bid);
  const formattedAskPrice = useFormattedGrfqQuotePrice(ask);

  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  return (
    <MyOrdersContainer>
      {rfq != null && bid != null && (
        <>
          {bid.numQuotes > 1 && <NumBids>{bid.numQuotes}</NumBids>}
          <MyBid>
            <QtyCell disabled={isPlatformCreationDisabled} data-testid={rfq.id}>
              {formatProductQty(
                bid.remaining_quantity,
                bid.product_codes?.[0],
                false,
                isDated,
              )}
            </QtyCell>
            <KillCell>
              <KillQuoteButton
                title="kill my bid"
                side="BUY"
                price={bid.price}
                data-testid={rfq.id}
                disabled={isPlatformKillDisabled}
                aria-label={`rfq_open_bid_cancel_${quoteIndex}`}
              />
            </KillCell>
            <BidPrice
              disabled={isPlatformCreationDisabled}
              data-testid={rfq.id}
              aria-label={`rfq_open_bid_price_${quoteIndex}`}
            >
              {formattedBidPrice}
            </BidPrice>
          </MyBid>
        </>
      )}
      {rfq != null && ask != null && (
        <>
          <MyAsk>
            <AskPrice
              disabled={isPlatformCreationDisabled}
              data-testid={rfq.id}
              aria-label={`rfq_open_ask_price_${quoteIndex}`}
            >
              {formattedAskPrice}
            </AskPrice>
            <KillCell>
              <KillQuoteButton
                title="kill my ask"
                side="SELL"
                price={ask.price}
                data-testid={rfq.id}
                disabled={isPlatformKillDisabled}
                aria-label={`rfq_open_ask_cancel_${quoteIndex}`}
              />
            </KillCell>
            <QtyCell data-testid={rfq.id} disabled={isPlatformCreationDisabled}>
              {formatProductQty(
                ask.remaining_quantity,
                ask.product_codes?.[0],
                false,
                isDated,
              )}
            </QtyCell>
          </MyAsk>
          {ask.numQuotes > 1 && <NumAsks>{ask.numQuotes}</NumAsks>}
        </>
      )}
    </MyOrdersContainer>
  );
}

interface PriceProps {
  readonly disabled: boolean;
  readonly children: string;
}

function BidPrice(props: PriceProps) {
  const { children, disabled = false, ...restProps } = props;
  return (
    <PriceCell disabled={disabled}>
      <BidPriceContainer {...restProps}>
        {enhanceNegativeSign(children)}
      </BidPriceContainer>
    </PriceCell>
  );
}

function AskPrice(props: PriceProps) {
  const { children, disabled, ...restProps } = props;
  return (
    <PriceCell disabled={disabled}>
      <AskPriceContainer {...restProps}>
        {enhanceNegativeSign(children)}
      </AskPriceContainer>
    </PriceCell>
  );
}

const PriceContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 24px;
  border-radius: 3px;
  background: black;
  font-weight: 600;
  white-space: nowrap;
`;

const BidPriceContainer = styled(PriceContainer)`
  color: ${lima};
`;

const AskPriceContainer = styled(PriceContainer)`
  color: ${razzmatazz};
`;
